import React from "react";
import { motion } from "framer-motion";
import {
  FaCode,
  FaDatabase,
  FaTools,
  FaLaptopCode,
  FaHtml5,
  FaCss3Alt,
  FaReact,
  FaNodeJs,
  FaJs,
  FaLinux,
} from "react-icons/fa";
import {
  SiTailwindcss,
  SiRedux,
  SiNextdotjs,
  SiExpress,
  SiCplusplus,
  SiTypescript,
  SiKotlin,
  SiMongodb,
  SiPostgresql,
  SiGithub,
} from "react-icons/si";

const skillsData = [
  {
    category: "Development",
    icon: <FaLaptopCode />,
    skills: [
      { name: "HTML", icon: <FaHtml5 /> },
      { name: "CSS", icon: <FaCss3Alt /> },
      { name: "Tailwind CSS", icon: <SiTailwindcss /> },
      { name: "React", icon: <FaReact /> },
      { name: "Redux", icon: <SiRedux /> },
      { name: "Next.js", icon: <SiNextdotjs /> },
      { name: "Node", icon: <FaNodeJs /> },
      { name: "Express", icon: <SiExpress /> },
    ],
  },
  {
    category: "Programming",
    icon: <FaCode />,
    skills: [
      { name: "C/C++", icon: <SiCplusplus /> },
      { name: "JavaScript", icon: <FaJs /> },
      { name: "TypeScript", icon: <SiTypescript /> },
      { name: "Kotlin", icon: <SiKotlin /> },
    ],
  },
  {
    category: "Database",
    icon: <FaDatabase />,
    skills: [
      { name: "MongoDB", icon: <SiMongodb /> },
      { name: "PostgreSQL", icon: <SiPostgresql /> },
    ],
  },
  {
    category: "Tools & Platforms",
    icon: <FaTools />,
    skills: [
      { name: "Linux", icon: <FaLinux /> },
      { name: "GitHub", icon: <SiGithub /> },
    ],
  },
];

const SkillCard: React.FC<{
  name: string;
  icon: JSX.Element;
}> = ({ name, icon }) => (
  <div className="bg-gray-800 rounded-md p-3 flex items-center space-x-2">
    <div className="text-2xl text-teal-400">{icon}</div>
    <p className="text-sm font-medium text-gray-300">{name}</p>
  </div>
);

const CategoryCard: React.FC<{
  category: string;
  icon: JSX.Element;
  skills: { name: string; icon: JSX.Element }[];
}> = ({ category, icon, skills }) => (
  <motion.div
    className="bg-gray-900 rounded-lg p-5 duration-300"
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3 }}
  >
    <div className="flex items-center mb-4">
      <div className="text-3xl text-teal-400 mr-3">{icon}</div>
      <h3 className="text-2xl font-semibold text-teal-400">{category}</h3>
    </div>
    <div className="grid grid-cols-2 gap-2">
      {skills.map((skill, skillIndex) => (
        <SkillCard key={skillIndex} {...skill} />
      ))}
    </div>
  </motion.div>
);

const Skills: React.FC = () => {
  return (
    <section id="skills" className="py-20 bg-gray-900 text-gray-300">
      <div className="container mx-auto px-4">
        <motion.h2
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl font-bold mb-12 text-center text-teal-400"
        >
          My Skills
        </motion.h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {skillsData.map((category, index) => (
            <CategoryCard key={index} {...category} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Skills;
